import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-neutral-900 text-white hover:bg-black/90 shadow-sm",
        primary: "bg-neutral-900 text-white hover:bg-black/90 border border-transparent shadow-[0px_-1px_0px_0px_#FFFFFF40_inset,_0px_1px_0px_0px_#FFFFFF40_inset]",
        secondary: "bg-neutral-100 text-neutral-900 hover:bg-neutral-200 shadow-sm",
        outline: "bg-white border border-neutral-900 text-neutral-900 hover:bg-neutral-100 hover:text-neutral-900",
        ghost: "hover:bg-neutral-100 hover:text-neutral-900",
        simple: "bg-transparent hover:bg-gray-100 text-neutral-900 dark:text-white dark:hover:bg-neutral-800",
        destructive: "bg-red-500 text-white hover:bg-red-600 shadow-sm",
        success: "bg-green-600 text-white hover:bg-green-700 shadow-sm",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "px-4 py-2",
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4",
        lg: "h-12 px-6 text-base",
        icon: "h-9 w-9 p-0",
      },
      roundedness: {
        default: "rounded-md",
        full: "rounded-full",
        lg: "rounded-lg",
        none: "rounded-none"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      roundedness: "full"
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  as?: React.ElementType;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, roundedness, asChild = false, as: Component, children = "Get Started", ...props }, ref) => {
    const Comp = asChild ? Slot : Component || "button";
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, roundedness, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
